import { memo } from 'react';
import { ContentType } from '@amzn/awscat-pattern-library-service-model';
import { Box, Header } from '@amzn/awsui-components-core';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

import styles from './ContentNavigator.module.scss';
import { useContentNavigatorViewModel } from './ContentNavigatorViewModel';
import AddNewPageButton from './AddNewPageButton';
import NavigatorNode, { NavigatorSiblingItems } from './NavigatorNode';
import EditPageButton from './EditPageButton';

/**
 * Minimal side navigator for an APGL document. Renders nothing until the
 * {@link ContentEditorService} has loaded a document; then renders the content
 * tree. Clicking a list item sets the URL hash to `#contents?section=<id>`
 * where `<id>` is the node's `href` (or `id` when `href` is absent).
 */
const ContentNavigator = memo(function ContentNavigator() {
  const vm = useContentNavigatorViewModel();

  if (vm.status === 'hidden') return null;

  // Only Guide has pages. Pattern uses sections (no page hierarchy).
  const isGuide = vm.contentType === ContentType.Guide;
  const isShowAddPage = vm.isEditMode && isGuide;
  const guideRootItem = isGuide ? vm.topLevelListItems[0] : undefined;
  const guidePageItems = isGuide ? vm.topLevelListItems.slice(1) : [];

  return (
    <nav className={styles.nav} aria-label="Document contents">
      <div className={styles.heading}>
        <Header variant="h2">{vm.heading}</Header>
      </div>

      <SimpleBar className={styles.scrollArea}>
        {vm.isEditMode ? (
          <div className={styles.editHeader}>
            <span className={styles.editLabel}>Pages</span>
            <span className={styles.editIcons}>
              {isShowAddPage && (
                <>
                  <AddNewPageButton />
                  <EditPageButton />
                </>
              )}
            </span>
          </div>
        ) : (
          <Box padding={{ top: 's' }} />
        )}

        <ul className={styles.list}>
          {guideRootItem ? (
            <>
              <NavigatorNode
                item={guideRootItem}
                onSelect={vm.onListItemSelected}
                isPageReorderEnabled={vm.isPageReorderEnabled}
                onPageOrderChanged={vm.onPageOrderChanged}
                isShowDragHandlePlaceholder={vm.isPageReorderEnabled}
              />
              <NavigatorSiblingItems
                items={guidePageItems}
                parentPageId={guideRootItem.contentNode.id}
                onSelect={vm.onListItemSelected}
                isPageReorderEnabled={vm.isPageReorderEnabled}
                onPageOrderChanged={vm.onPageOrderChanged}
              />
            </>
          ) : (
            vm.topLevelListItems.map((item) => (
              <NavigatorNode
                key={item.contentNode.href || item.contentNode.id}
                item={item}
                onSelect={vm.onListItemSelected}
                isPageReorderEnabled={false}
                onPageOrderChanged={vm.onPageOrderChanged}
              />
            ))
          )}
        </ul>
      </SimpleBar>
    </nav>
  );
});

export default ContentNavigator;
